import { DeepObjectMerge } from "./deep_object_merge"
import type {
  AnyObject,
  DeepPartial,
  ExcludeMethod,
  Many,
} from "./types/custom_type.types"

export type ProvideEntity<E, U extends keyof E> = ExcludeMethod<
  Omit<E, U> & {
    [K in U]?: E[K]
  }
>

const DEFAULT_FACTORY_FUNC_OPTIONS = {
  defaultValue: true,
  excludeExtraProp: true,
}

type InputType<E, U extends keyof E, V extends boolean> = V extends false
  ? ProvideEntity<E, U>
  : DeepPartial<E>

export const modelFactory =
  <
    E extends AnyObject,
    U extends keyof E = never,
    V extends boolean = false,
    CE = never
  >(
    classModel: new (..._arguments: any[]) => E,
    factoryOptions?: {
      autoGeneratedProp?: Array<Many<U>>
      allAutoGenerated?: V
      excludeExtraProp?: boolean
    },
    customMapper?: (input: InputType<E, U, V>) => CE,
  ) =>
    (
      input: InputType<E, U, V>,
      options?: typeof DEFAULT_FACTORY_FUNC_OPTIONS,
      customMapperOverride?: (input: InputType<E, U, V>) => CE,
    ): never extends CE ? E : CE => {

      // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
      let { allAutoGenerated, autoGeneratedProp, excludeExtraProp } = {
      // allAutoGenerated: false,
      // autoGeneratedProp: [],
        excludeExtraProp: true,
        ...factoryOptions,
      }
      const { defaultValue, excludeExtraProp: excludeExtraPropertyOverride } =
      Object.assign(DEFAULT_FACTORY_FUNC_OPTIONS, options)
      excludeExtraProp = excludeExtraPropertyOverride ?? excludeExtraProp

      if (typeof classModel !== `function`)
        throw new TypeError(`modelFactory: classModel must be a constructor.`)

      const model = new classModel() //! NOTE: DON'T SPREAD IT! IT WILL BREAK THE REFLECT DECORATOR.

      if (!defaultValue) {
        for (const key of Object.keys(model)) {
          if (typeof model[key] === `function`) model[key] = undefined
        }
      }

      if (excludeExtraProp) {
        for (const key in model) {
          if (
            Object.prototype.hasOwnProperty.call(model, key) &&
          typeof input === `object` &&
          key in input
          ) {
            model[String(key)] = input?.[String(key)] ?? model[String(key)]
            if (!model[String(key)] && input?.[String(key)] === null)
              model[String(key)] = null as any
          }

        //! NOTE: Cannot throw detect, optional (propertyD?: string), will always throw error...
        // if (!allAutoGenerated && !(<string[] | undefined>autoGeneratedProp)?.includes(key as string) && isNull(objectModel[key]))
        //   throw new Error(`${ key } is not provided!`)
        }
      } else {
        const merged = DeepObjectMerge(model, input) as E
        for (const key in merged) {
          if (Object.prototype.hasOwnProperty.call(merged, key))
            model[key] = merged[key]
        }
      }

      const mapper = (modelTarget: E) => {
        let modelMapped: never extends CE ? E : CE = modelTarget //! NOTE: DON'T SPREAD IT! IT WILL BREAK THE REFLECT DECORATOR.

        if (typeof customMapper === `function`)
          modelMapped = customMapper(
          modelMapped as InputType<E, U, V>,
          ) as never extends CE ? E : CE

        if (typeof customMapperOverride === `function`)
          modelMapped = customMapperOverride(
          modelMapped as InputType<E, U, V>,
          ) as never extends CE ? E : CE

        return modelMapped
      }

      return mapper(model) //! NOTE: DON'T SPREAD IT! IT WILL BREAK THE REFLECT DECORATOR.
    }

export const modelCreator = modelFactory
